import { useMemo } from "react";
import { getSnowplowConfig, SnowplowProvider } from "@simplybusiness/services";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Provider } from "jotai";
import { ErrorBoundary } from "../../components/ErrorBoundary";
import {
  getZendeskKeys,
  InterventionsProviderWithAnswers,
  TrustpilotProvider,
  ZendeskProvider,
  getCtmScriptProps,
  getTealiumScriptProps,
  CtmTealiumProvider,
  CtmTrackingProvider,
} from "../../contexts";
import { type PageProps } from "./types";

const isCTM = (site: string) => site === "comparethemarket";

export function PageProviders(props: PageProps) {
  // Extract site from configuration
  const { configuration } = props;
  const { site, vertical } = configuration;

  // Snowplow analytics
  const snowplowProps = getSnowplowConfig(props);
  const duid = snowplowProps?.duid;
  // ZendeskChat
  const zendeskProps = getZendeskKeys(props);
  // Create QueryClient only once, not on every render
  const queryClient = useMemo(() => new QueryClient(), []);
  // Disable interventions polling if specified
  const disablePolling = configuration.disableIdmPolling === "true";
  // CompareTheMarket custom tracking
  const ctmScriptProps = isCTM(site) ? getCtmScriptProps(props) : undefined;
  const tealiumScriptProps = isCTM(site)
    ? getTealiumScriptProps(props)
    : undefined;

  return (
    <Provider>
      <QueryClientProvider client={queryClient}>
        {zendeskProps && <ZendeskProvider {...zendeskProps} />}
        {ctmScriptProps && <CtmTrackingProvider {...ctmScriptProps} />}
        {tealiumScriptProps && <CtmTealiumProvider {...tealiumScriptProps} />}
        <SnowplowProvider scripts={snowplowProps!}>
          <InterventionsProviderWithAnswers
            site={site}
            duid={duid}
            vertical={vertical}
            disablePolling={disablePolling}
          >
            <ErrorBoundary>
              <TrustpilotProvider>{props.children}</TrustpilotProvider>
            </ErrorBoundary>
          </InterventionsProviderWithAnswers>
        </SnowplowProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </Provider>
  );
}
